import "./TOCPanel.css";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import { useLearnCtx } from "./LearnPage";
import logoRect from "../../images/LogoRect.svg";
import { Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  updateProgressDeleteBookmark,
  updateProgressContentSeen,
} from "../../actions/progress";
import { EDCOMP_TITLE } from "../../constants/frameConstants";
import { VERSION_NUMBER } from "../../constants/versionNumber";
import {
  SIGNAL_START_STOPWATCH,
  STORE_OPEN_PROBLEM_SET,
} from "../../constants/actionConstants";
import { isVisibleInViewport } from "../Global/utils";
import { calcPercentRead } from "../Global/utils.js";
import {
  calcPercentExercisesComplete,
  calculate_progress,
  preprocess_problems_solved,
} from "../Global/ProblemUtils";
import { parseTitle } from "../../utils/learnUtils.js";
import StopWatch from "./StopWatch.jsx";
import { useFocusTrap, useRestoreFocus } from "../../hooks/useFocusTrap";

/** Map level → indent class */
const INDENT = {
  chapter: "toc__item--chapter",
  section: "toc__item--section",
  subsection: "toc__item--subsection",
};

// Tracks what which of the headings_in is currently active on the screen
const useIntersectionObserver = (headings_in, setActiveId, activeId) => {
  const headingElementsRef = React.useRef({});

  useEffect(() => {
    // Callback for IntersectionObserver
    const callback = (headings) => {
      headingElementsRef.current = headings.reduce((map, headingElement) => {
        map[headingElement.target.id] = headingElement;
        return map;
      }, headingElementsRef.current);

      // Get all headings that are currently visible on the page
      const visibleHeadings = [];
      Object.keys(headingElementsRef.current).forEach((key) => {
        const headingElement = headingElementsRef.current[key];
        if (headingElement.isIntersecting) visibleHeadings.push(headingElement);
      });

      const getIndexFromId = (id) =>
        headingElements.findIndex((heading) => heading.id === id);

      // If there is only one visible heading, this is our "active" heading
      if (visibleHeadings.length === 1) {
        setActiveId(visibleHeadings[0].target.id);
        // If there is more than one visible heading,
        // choose the one that is closest to the top of the page
      } else if (visibleHeadings.length > 1) {
        const sortedVisibleHeadings = visibleHeadings.sort(
          (a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id),
        );

        setActiveId(sortedVisibleHeadings[0].target.id);
      }

      // If there are no visible headings, and we are scrolling back up, we want to make sure
      // the correct header is highlighted.
      if (visibleHeadings.length === 0) {
        const activeElement = headingElements.find((el) => el.id === activeId);
        const activeIndex = headingElements.findIndex(
          (el) => el.id === activeId,
        );

        const activeIdYcoord = activeElement?.getBoundingClientRect().y;
        if (activeIdYcoord && activeIdYcoord > 150 && activeIndex !== 0) {
          setActiveId(headingElements[activeIndex - 1].id);
        }
      }
    };

    // Back to main code  -- create intersection observer and point to callback above
    // Considered visible if in top 60% of the screen.
    const observer = new IntersectionObserver(callback, {
      // root: document.querySelector("iframe"),
      rootMargin: "0px 0px -40% 0px",
    });

    // Find all of the heading elements in the actual HTML and tell the observer to keep an eye on them.
    const headingElements = headings_in.map((this_heading) => {
      return document.getElementById(this_heading.titleId);
    });
    headingElements.forEach((element) => {
      if (element != null) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings_in, setActiveId, activeId]);
};

const TOCPanel = ({ minMaxCallBack, onClose }) => {
  // Dispatch item
  const dispatch = useDispatch();
  const { id: courseId } = useParams();
  const { openEditBookmarkModal } = useLearnCtx();

  // This is the ID of the item that is currently highlighted
  const [activeId, setActiveId] = useState();
  const tocNavRef = useRef(null);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [dotTooltip, setDotTooltip] = useState(null);

  // Between the content and the course structure, we have all information needed
  const content = useSelector((state) => state.ContentReducer.content);
  const courseStructure = useSelector(
    (state) => state.CourseStructureReducer.courseStructure,
  );

  // Retrieve bookmarks from reducer and sort
  const bookmarks = useSelector((state) => state.ProgressReducer.bookmarks);
  var sortedBookmarks = structuredClone(bookmarks);
  sortedBookmarks = sortedBookmarks.sort((a, b) => a.index - b.index);

  // Run through content extracting information for table of contents items
  const headings = useMemo(() => {
    const result = [];
    for (let c_section = 0; c_section < content?.length; c_section++) {
      if (content[c_section].hasOwnProperty("title")) {
        content[c_section].subsections.forEach((subsection) => {
          subsection.frames.forEach((frame) => {
            frame.contents.forEach((component) => {
              if (component.type === EDCOMP_TITLE) {
                const match_array = [
                  ...component.title.matchAll(
                    new RegExp("\\\\tag\\{([^\\}]*)\\}([\\s\\S]*)", "gm"),
                  ),
                ];
                const sec_chap_no = match_array[0][1];
                const titleId = "id:" + sec_chap_no;
                result.push({
                  title: component.title,
                  indentLevel: component.data.level,
                  contentId: component._id,
                  titleId: titleId,
                });
              }
            });
          });
        });
      } else {
        result.push({
          title: courseStructure[c_section]?.title,
          start_state: 0,
          indentLevel: courseStructure[c_section]?.type,
          n_states: 1,
          titleId: "",
        });
      }
    }
    return result;
  }, [content, courseStructure]);

  useIntersectionObserver(headings, setActiveId, activeId);

  // Scroll the TOC nav to keep the active heading button visible
  useEffect(() => {
    if (!activeId || !tocNavRef.current) return;
    const activeBtn = tocNavRef.current.querySelector(".toc__item--active");
    activeBtn?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  // Retrieve time and format into hours / minutes / seconds
  const previousElapsedTime = useSelector(
    (state) => state.ProgressReducer.elapsedTimeCopy,
  );
  const formatTime = (timer) => {
    const hours = Math.floor(timer / 3600000)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((timer / 60000) % 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor((timer / 1000) % 60)
      .toString()
      .padStart(2, "0");
    return { hours, minutes, seconds };
  };
  const { hours, minutes, seconds } = formatTime(previousElapsedTime);

  function doScroll(element, pause = 0) {
    return new Promise((resolve) => {
      if (element) {
        const container = document.querySelector(".learn__canvas-panel");
        if (container) {
          const targetScrollTop =
            container.scrollTop +
            element.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            container.clientHeight * 0.2;
          container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
        } else {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        // Briefly highlight the element we scrolled to
        element.classList.add("toc__bm-item-highlight");
        setTimeout(() => {
          element.classList.remove("toc__bm-item-highlight");
          resolve();
        }, pause || 2000);
      } else {
        resolve(); // Resolve immediately if the element is not found
      }
    });
  }

  const openProblem = async (set_id, problem_index) => {
    // Find the element to scroll to
    const element = document.getElementById(set_id);
    if (!isVisibleInViewport(element)) {
      await doScroll(element, 1000);
    }
    dispatch({
      type: STORE_OPEN_PROBLEM_SET,
      payload: { id: set_id, open_problem: problem_index },
    });
  };

  // Retrieve information about what has already been read
  const progressContentSeen = useSelector(
    (state) => state.ProgressReducer.contentSeen,
  );

  // Mark heading as seen only after a 2-second dwell, so scrolling past headings doesn't count
  useEffect(() => {
    if (!activeId) return;
    const heading = headings.find((h) => h.titleId === activeId);
    if (!heading?.contentId) return;
    const timer = setTimeout(() => {
      dispatch(
        updateProgressContentSeen(
          courseId,
          heading.contentId,
          calcPercentRead(content, progressContentSeen),
        ),
      );
    }, 2000);
    return () => clearTimeout(timer);
  }, [activeId, headings, content, progressContentSeen, courseId, dispatch]);
  const [percentComplete, setPercentComplete] = useState(0);
  const [percentRead, setPercentRead] = useState(0);
  const [problemsSolved, setProblemsSolved] = useState([]);
  const problemsTriedAndAnswers = useSelector(
    (state) => state.ProgressReducer.problemsTried,
  );
  const problemIds = useSelector((state) => state.ContentReducer.problemIds);

  // Calculate percent read to display, but also call action to store in database
  useEffect(() => {
    const percentRead = calcPercentRead(content, progressContentSeen);
    setPercentRead(percentRead);
  }, [content, progressContentSeen]);

  // Calculate percent problems completed
  useEffect(() => {
    const percentCorrectExercises = calcPercentExercisesComplete(
      problemIds,
      problemsTriedAndAnswers,
    );
    setPercentComplete(percentCorrectExercises);
    const problemsTried = problemsTriedAndAnswers.map((a) => a.problemId);
    setProblemsSolved(preprocess_problems_solved(problemIds, problemsTried));
  }, [problemsTriedAndAnswers, problemIds]);

  return (
    <div className="toc">
      {/* Logo + Timer */}
      <div className="toc__logo">
        <div>
          <Link to="/" className="toc__logo-link">
            <img src={logoRect} alt="IClimbTrees" className="toc__logo-img" />
          </Link>
          <button
            className="toc__version-btn"
            onClick={() => {
              setAboutModalOpen(true);
              // Signal that we have interacted
              dispatch({
                type: SIGNAL_START_STOPWATCH,
                payload: Math.random(),
              });
            }}
          >
            v.&nbsp;{VERSION_NUMBER}&nbsp;(report issue)
          </button>
        </div>
        <div className="toc__timer" aria-live="polite">
          <span className="toc__timer-icon">⏱</span>
          <span className="toc__timer-value">
            {hours}:{minutes}:{seconds}
          </span>
        </div>
      </div>

      {/* TOC items + Bookmarks in the same scroll container */}
      <nav className="toc__nav" ref={tocNavRef} aria-label="Table of contents">
        {headings.map((item, i) => {
          const isActive = activeId === item.titleId;
          const isRead = progressContentSeen.includes(item.contentId);
          const { tag, name } = parseTitle(item.title);
          return (
            <button
              key={`${item.contentId}-${i}`}
              className={[
                "toc__item",
                INDENT[item.indentLevel] ?? "",
                isActive ? "toc__item--active" : "",
                isRead ? "toc__item--read" : "",
              ].join(" ")}
              onClick={() => {
                doScroll(document.getElementById(item.titleId));
                onClose?.();
              }}
              title={item.title}
            >
              {tag ? (
                <>
                  <span className="toc__item-tag">{tag}</span>
                  <span className="toc__item-name">{name}</span>
                </>
              ) : (
                <span className="toc__item-name">{name}</span>
              )}
            </button>
          );
        })}

        {/* Bookmarks — sit directly below the last TOC item */}
        {sortedBookmarks.length > 0 && (
          <div className="toc__bookmarks">
            <div className="toc__bookmarks-header">Bookmarks</div>
            {sortedBookmarks.map((bm, i) => (
              <div key={`${bm.contentId}-${i}`} className="toc__bm-item">
                <button
                  className="toc__bm-scroll-btn"
                  onClick={() => {
                    doScroll(document.getElementById(bm.scrollId));
                    onClose?.();
                  }}
                  title={`${i + 1} ` + bm.name}
                >
                  <span className="toc__bm-icon" aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M3 2h8v10L7 9.5 3 12V2z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="toc__bm-name">{bm.name}</span>
                </button>
                <div className="toc__bm-actions">
                  <button
                    className="toc__bm-action-btn"
                    aria-label="Edit bookmark"
                    title="Edit bookmark"
                    onClick={() => openEditBookmarkModal(bm)}
                  >
                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="toc__bm-action-btn toc__bm-action-btn--delete"
                    aria-label="Delete bookmark"
                    title="Delete bookmark"
                    onClick={() => {
                      if (window.confirm(`Delete bookmark "${bm.name}"?`)) {
                        dispatch(
                          updateProgressDeleteBookmark(courseId, bm._id),
                        );
                      }
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2 4h10M5 4V2.5h4V4M5.5 6.5v4M8.5 6.5v4M3 4l.8 8h6.4L11 4"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </nav>

      {/* Progress + problem dots */}
      <div className="toc__footer">
        <ProgressBar
          label="Reading progress"
          pct={percentRead}
          color="var(--amber)"
        />
        <ProgressBar
          label="Problems answered"
          pct={percentComplete}
          color="var(--amber)"
        />

        <div className="toc__problems">
          {problemsSolved.map((problemSet, index) => {
            return (
              <ProblemSetDots
                key={index}
                ps={problemSet}
                onDotHover={setDotTooltip}
                onClose={onClose}
              />
            );
          })}
        </div>
      </div>

      {/* Floating dot tooltip */}
      {dotTooltip && (
        <div
          className="toc__dot-tooltip"
          style={{ left: dotTooltip.x, top: dotTooltip.y }}
        >
          {dotTooltip.label}
        </div>
      )}

      {/* Version / issue modal */}
      {aboutModalOpen && (
        <VersionModal onClose={() => setAboutModalOpen(false)} />
      )}
      <StopWatch />
    </div>
  );
};

/* ─── Sub-components ────────────────────────────────────────── */

function ProgressBar({ label, pct, color }) {
  return (
    <div className="toc__progress">
      <div className="toc__progress-header">
        <span className="toc__progress-label">{label}</span>
        <span className="toc__progress-pct">{pct.toFixed(0)}%</span>
      </div>
      <div className="toc__progress-track">
        <div
          className="toc__progress-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

function ProblemSetDots({ ps, onDotHover, onClose }) {
  const dispatch = useDispatch();
  const hideTimeoutRef = useRef(null);

  const scrollToElement = (element) => {
    return new Promise((resolve) => {
      if (!element) {
        resolve();
        return;
      }
      const container = document.querySelector(".learn__canvas-panel");
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        const targetScrollTop =
          container.scrollTop + relativeTop - container.clientHeight * 0.25;
        container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
        container.addEventListener("scrollend", resolve, { once: true });
      } else {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        document.addEventListener("scrollend", resolve, { once: true });
      }
    });
  };

  const handleClick = async (idx) => {
    onClose?.();
    // Signal that we have interacted
    dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });

    const element = document.getElementById(ps[idx].set_id);
    if (!isVisibleInViewport(element)) {
      await scrollToElement(element);
    }
    dispatch({
      type: STORE_OPEN_PROBLEM_SET,
      payload: { id: ps[idx].set_id, open_problem: idx },
    });
  };

  return (
    <div className="toc__ps-group">
      {ps?.map((p, idx) => {
        const answered = p.solved;
        const label = `${p.set_name} (${idx + 1})`;
        return (
          <button
            key={p._id}
            className={`toc__ps-dot ${answered ? "toc__ps-dot--done" : ""}`}
            aria-label={`${label}${answered ? " (answered)" : ""}`}
            onMouseEnter={(e) => {
              clearTimeout(hideTimeoutRef.current);
              const rect = e.currentTarget.getBoundingClientRect();
              onDotHover({ label, x: rect.left + rect.width / 2, y: rect.top });
            }}
            onMouseLeave={() => {
              hideTimeoutRef.current = setTimeout(() => onDotHover(null), 120);
            }}
            onClick={() => handleClick(idx)}
          />
        );
      })}
    </div>
  );
}

/**
 * Full-screen modal with version and issue-reporting info.
 * @param {{ onClose: () => void }} props
 */
function VersionModal({ onClose }) {
  useRestoreFocus();
  const containerRef = useRef(null);
  const closeRef = useRef(null);
  useFocusTrap(containerRef, true, false);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="ver-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Version information"
    >
      <div
        ref={containerRef}
        className="ver-modal__card"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={logoRect} alt="IClimbTrees" className="ver-modal__logo" />

        <p className="ver-modal__heading">
          v.{VERSION_NUMBER}&nbsp;&mdash;&nbsp;&copy;&nbsp;2026 IClimbTrees. All
          rights reserved.
        </p>

        <p className="ver-modal__body">
          Please report issues with the website via GitHub or by sending an
          e-mail to{" "}
          <a
            href="mailto:iclimbtreesmail@gmail.com"
            className="ver-modal__link"
          >
            iclimbtreesmail@gmail.com
          </a>
          . Include as many details as you can, including the version number (
          {VERSION_NUMBER}), browser type, and attach images if there are user
          interface problems or things look odd.
        </p>

        <p className="ver-modal__body">
          For errata in the content, add a comment using the speech bubble links
          to the right of the main text.
        </p>

        <button ref={closeRef} className="ver-modal__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export default TOCPanel;

// TODO:  Add edit and delete bookmark
